/**
 * Event-console derivations — the pure read model every KPI, the per-type
 * table, and the present/after headline derive from. Ported from
 * the design studio's features/event-console/event-console.state.ts's
 * buildConsoleViewModel, which proved the shape against fixtures; this is
 * the same computation over real state cells.
 *
 * `consoleOrders`/`consoleTickets` are written by web's own
 * console.sync.ts (the Firestore listener), scoped to exactly
 * `consoleEventId`'s subcollections — Order carries no eventId field (it
 * relates by Firestore path), so `buildConsoleViewModel` never filters on
 * one; the scoping already happened at the listener.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { resolveLocalizedText } from "@momo/i18n";
import { Event, Order, type Ticket, TicketType } from "@momo/types";
import { uiLanguage } from "../../i18n/localize.ts";
import { locationsState } from "../../storefront/locations.state.ts";
import { eventsState } from "../events.state.ts";
import { ticketTypesState } from "../ticket-types/ticket-types.state.ts";

/** Orders carry no currency field and there is no workspace reader for the
 *  checkout currency in this package yet; every money figure the console
 *  prints pins EUR here, so the money cell and the breakdown it opens can
 *  never pin two different ones. */
export const CURRENCY = "EUR";

export type Lifecycle = Event.Lifecycle;

export type OrderRow = Order.Order & {
	readonly id: string;
	readonly receiptUrl?: string | undefined;
};
export type TicketRow = Ticket.Ticket & { readonly id: string };

export type TypeRow = {
	readonly id: string;
	readonly name: string;
	readonly price: number;
	readonly sold: number;
	readonly remaining: number;
	readonly capacity: number;
	readonly revenue: number;
	readonly guestSold: number;
	/** The type's own EFFECTIVE entry window (TicketType.resolveEntryWindow) in
	 *  epoch millis — when it opens the DOOR, never when it may be BOUGHT.
	 *  Millis rather than the Timestamp because every reader compares or
	 *  formats it and none passes it back to Firestore. The calendar day it
	 *  falls on is DERIVED from it at render time and never stored, exactly as
	 *  ticket-type.entity.ts states: a stored day beside the window is a second
	 *  representation free to contradict it. */
	readonly entryWindow: { readonly opensAt: number; readonly closesAt: number };
	/** resolveEffectiveTimezone(type, event, locations) — the type's own venue
	 *  override wins over the event's, so a type in a different-timezone room
	 *  lands on its own calendar day, not the event-wide one. */
	readonly timezone: string;
};

export type ExceptionKind =
	| "refundedOrders"
	| "authorisedNeverPaid"
	| "cancelledOnPaid";

/** One non-zero business exception the dashboard's warning line names —
 *  refunds, orders authorised but never paid, and cancellations that hit an
 *  already-paid order. */
export type ExceptionEntry = {
	readonly kind: ExceptionKind;
	readonly count: number;
};

export type ConsoleViewModel = {
	readonly eventId: string;
	readonly lifecycle: Lifecycle;
	readonly now: Date;
	readonly hasTicketTypes: boolean;
	/** The event carries ticket types and none of them is offered to a
	 *  stranger with the link (TicketType.resolveClaimableBy, the shop's own
	 *  reading) — every one is staff-only or code-gated, so the public shop
	 *  lists nothing. Read off the catalog alone, never inventory: a sold-out
	 *  public type still makes this false. */
	readonly publicShopEmpty: boolean;
	readonly totalCapacity: number;
	readonly totalSold: number;
	/** Active ticket quantities partitioned by the order's origin. These are
	 *  disjoint and their sum is `totalSold`. */
	readonly soldTickets: number;
	readonly guestTickets: number;
	/** Σ `line.amount` − `refundedAmount` over paid AND refunded orders — the
	 *  gross the buyers actually handed over and kept handed over. Gross, not
	 *  net: no VAT or fee has been taken out of it. An order whose refund
	 *  cannot be attributed to lines is excluded whole (see
	 *  `unattributableOrders`). */
	readonly grossTakenCents: number;
	/** Σ `vatCents(order)` − `refundedVatCents(...)` over the same orders as
	 *  `grossTakenCents` — the VAT the retained gross still carries, per line
	 *  at each line's own rate, the § 14 invoice's own split. */
	readonly vatOwedCents: number;
	/** The one VAT rate every contributing line charges at, or null when more
	 *  than one does — the question an explanation must answer before it names
	 *  a percentage, asked as a set over `lines[].vatRate` exactly as the
	 *  checkout's own price disclosure asks it, since across a mixed event no
	 *  single percentage would be honest. Only lines charging something count:
	 *  a €0 comped line still carries its type's nominal rate, which governs
	 *  no money and must not read as a second rate. Null too where nothing was
	 *  taken at all — no rate governs a gross of zero, and the wording that
	 *  names no figure is the only one that cannot be wrong about one. */
	readonly governingVatRate: number | null;
	/** Σ `feeCents(order)` over the PSP-charged orders whose fee the provider
	 *  has reported. A LOWER bound while `feeOrdersReported <
	 *  feeOrdersTotal` — fees only grow as reports land; never includes an
	 *  unreported order as 0. Refunded orders included: acquiring fees are
	 *  not returned on refund. */
	readonly feeReportedCents: number;
	/** How many included orders have a PSP leg (`payment != null`) — the
	 *  denominator the fee bounds are read against. 0 means the event took no
	 *  PSP payment at all, so fees are exactly 0 and `youKeepCents` is exact. */
	readonly feeOrdersTotal: number;
	/** How many of `feeOrdersTotal` the provider has reported
	 *  (`feeCents(order) != null` — an empty report counts, it is a genuine
	 *  0). Equal to the total → every figure is exact; 0 with a non-zero
	 *  total → no fee figure exists yet. */
	readonly feeOrdersReported: number;
	/** Orders whose refund could not be attributed to lines
	 *  (`refundedVatCents` returned null) — each is excluded whole from ALL
	 *  four money figures above, the same refusal the correction PDF makes,
	 *  and counted here so the surface can say so. */
	readonly unattributableOrders: number;
	/** gross − VAT − fees. Exact when `feeOrdersTotal === 0` (all-free
	 *  event: fees are exactly 0) or `feeOrdersReported === feeOrdersTotal`.
	 *  With partial reports it is still gross − VAT − `feeReportedCents`, but
	 *  an UPPER bound — the reader must present it as "at most", carried by
	 *  the two counts. Null when `feeOrdersTotal > 0 && feeOrdersReported
	 *  === 0`: never a figure that pretends the PSP was free. */
	readonly youKeepCents: number | null;
	readonly authorisedAmount: number;
	readonly last7d: number;
	readonly prev7d: number;
	readonly daysToGo: number;
	readonly typeRows: readonly TypeRow[];
	/** Every type the event carries, addons included, keyed by id — the same
	 *  sold/revenue counters `typeRows` reads, over the whole catalog rather
	 *  than the ticket-kind subset. An addon's lines are counted in the very
	 *  same pass, so dropping them here would discard a figure the pass had
	 *  already produced and leave a row unable to state a number that exists.
	 *  `typeRows` stays ticket-only because the capacity aggregates beside it
	 *  measure seats, and an addon has none. */
	readonly figuresByType: ReadonlyMap<string, TypeRow>;
	/** Every non-zero exception, in the fixed order the dashboard's warning
	 *  line displays them — refunded, authorised-never-paid, cancelled-on-paid.
	 *  Empty when the event has nothing to flag. */
	readonly exceptionEntries: readonly ExceptionEntry[];
	/** refundedOrders + cancelledOnPaid — the dashboard's stat-tile figure,
	 *  not the warning line's per-kind breakdown. */
	readonly refundedCancelledCount: number;
	readonly validTickets: number;
	readonly redeemedTickets: number;
	readonly arrivalsLast15Min: number;
	readonly attendanceKnown: boolean;
	readonly orders: readonly OrderRow[];
	readonly ticketsByOrder: ReadonlyMap<string, readonly TicketRow[]>;
};

const DAY_MS = 86_400_000;

function sumQtyInWindow(
	orders: readonly OrderRow[],
	fromMs: number,
	toMs: number,
): number {
	let sum = 0;
	for (const o of orders) {
		if (o.status !== "paid" && o.status !== "authorised") continue;
		const t = o.authorisedAt.toDate().getTime();
		if (t > fromMs && t <= toMs) {
			for (const line of o.lines) {
				if (line.kind === "ticket") sum += line.qty;
			}
		}
	}
	return sum;
}

export function buildConsoleViewModel(
	eventId: string,
	schedule: { start: Date; end: Date },
	types: readonly {
		id: string;
		name: string;
		price: number;
		capacity: number;
		kind: "ticket" | "addon";
		claimableBy?: TicketType.TicketType["claimableBy"];
		entryWindow: { opensAt: number; closesAt: number };
		timezone: string;
	}[],
	allOrders: readonly OrderRow[],
	allTickets: readonly TicketRow[],
	now: Date,
): ConsoleViewModel {
	const lifecycle = Event.resolveLifecycle(now, schedule);
	// allOrders/allTickets already carry only this event's own subcollections
	// (the sync listener scopes by Firestore path) — the remaining filter
	// guards only against a clock-skewed future-dated authorisedAt.
	const orders = allOrders.filter(
		(o) => o.authorisedAt.toDate().getTime() <= now.getTime(),
	);
	// Only a ticket whose order that guard excluded drops out. A ticket whose
	// order is not loaded still counts: the door figures read tickets alone,
	// so a session that may scan but not read orders still sees its door.
	const futureOrderIds = new Set(
		allOrders.filter((o) => !orders.includes(o)).map((o) => o.id),
	);
	const tickets = allTickets.filter((t) => !futureOrderIds.has(t.orderId));

	const ticketTypes = types.filter((t) => t.kind === "ticket");
	const totalCapacity = ticketTypes.reduce((s, t) => s + t.capacity, 0);

	const ticketsByOrder = new Map<string, TicketRow[]>();
	for (const t of tickets) {
		const bucket = ticketsByOrder.get(t.orderId);
		if (bucket) bucket.push(t);
		else ticketsByOrder.set(t.orderId, [t]);
	}

	const soldByType = new Map<string, number>();
	const revenueByType = new Map<string, number>();
	const guestSoldByType = new Map<string, number>();
	let soldTickets = 0;
	let guestTickets = 0;
	let grossTakenCents = 0;
	let vatOwedCents = 0;
	const vatRates = new Set<number>();
	let feeReportedCents = 0;
	let feeOrdersTotal = 0;
	let feeOrdersReported = 0;
	let unattributableOrders = 0;
	let authorisedAmount = 0;
	let refundedOrders = 0;
	let authorisedNeverPaid = 0;

	for (const o of orders) {
		if (o.status === "refunded") refundedOrders += 1;
		if (o.status === "authorised") authorisedNeverPaid += 1;

		// The money walk: paid AND refunded — a refunded order's gross nets to
		// what stayed, and its acquiring fee stays paid either way.
		if (o.status === "paid" || o.status === "refunded") {
			const refundedVat = Order.refundedVatCents(
				o,
				ticketsByOrder.get(o.id) ?? [],
			);
			if (refundedVat === null) {
				unattributableOrders += 1;
			} else {
				for (const line of o.lines) {
					grossTakenCents += line.amount;
					if (line.amount > 0) vatRates.add(line.vatRate);
				}
				grossTakenCents -= o.refundedAmount ?? 0;
				vatOwedCents += Order.vatCents(o) - refundedVat;
				if (o.payment != null) {
					feeOrdersTotal += 1;
					const fee = Order.feeCents(o);
					if (fee !== null) {
						feeOrdersReported += 1;
						feeReportedCents += fee;
					}
				}
			}
		}

		// The sold walk: paid and authorised only — a refunded order's seats
		// went back on sale, so it never counts as sold.
		if (o.status !== "paid" && o.status !== "authorised") continue;
		for (const line of o.lines) {
			// Authorised money is every kind of line — a booking fee and a
			// discount are authorised exactly as the admission they ride on is.
			if (o.status === "authorised") authorisedAmount += line.amount;
			// Only an admission names a type and a quantity, so every per-type
			// counter narrows onto that branch rather than away from a fee.
			if (line.kind !== "ticket") continue;
			if (o.origin === "guest") guestTickets += line.qty;
			else soldTickets += line.qty;
			soldByType.set(
				line.typeId,
				(soldByType.get(line.typeId) ?? 0) + line.qty,
			);
			if (o.status === "paid") {
				revenueByType.set(
					line.typeId,
					(revenueByType.get(line.typeId) ?? 0) + line.amount,
				);
				if (o.origin === "guest") {
					guestSoldByType.set(
						line.typeId,
						(guestSoldByType.get(line.typeId) ?? 0) + line.qty,
					);
				}
			}
		}
	}

	const totalSold = [...soldByType.values()].reduce((s, n) => s + n, 0);
	const last7d = sumQtyInWindow(
		orders,
		now.getTime() - 7 * DAY_MS,
		now.getTime(),
	);
	const prev7d = sumQtyInWindow(
		orders,
		now.getTime() - 14 * DAY_MS,
		now.getTime() - 7 * DAY_MS,
	);
	const daysToGo = Math.ceil(
		(schedule.start.getTime() - now.getTime()) / DAY_MS,
	);

	// Cancelled on an order that is STILL paid — a seat given back or voided
	// out of an order that otherwise stands, which is the exception this line
	// names. Every ticket of a wholly refunded order is cancelled too, and that
	// order is `refunded` rather than `paid`: counting those here reported the
	// whole refund a second time, once as an order and once per seat, and told
	// an operator that an ordinary refund was an anomaly.
	const paidOrderIds = new Set(
		orders.filter((o) => o.status === "paid").map((o) => o.id),
	);
	const cancelledOnPaid = tickets.filter(
		(t) => t.status === "cancelled" && paidOrderIds.has(t.orderId),
	).length;
	const validTickets = tickets.filter((t) => t.status !== "cancelled").length;
	const redeemedTickets = tickets.filter((t) => t.status === "redeemed").length;
	const fifteenMinAgoMs = now.getTime() - 15 * 60_000;
	const arrivalsLast15Min = tickets.filter(
		(t) =>
			t.status === "redeemed" &&
			t.redeemedAt &&
			t.redeemedAt.toDate().getTime() > fifteenMinAgoMs &&
			t.redeemedAt.toDate().getTime() <= now.getTime(),
	).length;

	const figuresByType = new Map<string, TypeRow>(
		types.map((t) => [
			t.id,
			{
				id: t.id,
				name: t.name,
				price: t.price,
				sold: soldByType.get(t.id) ?? 0,
				remaining: t.capacity - (soldByType.get(t.id) ?? 0),
				capacity: t.capacity,
				revenue: revenueByType.get(t.id) ?? 0,
				guestSold: guestSoldByType.get(t.id) ?? 0,
				entryWindow: t.entryWindow,
				timezone: t.timezone,
			},
		]),
	);
	const typeRows: TypeRow[] = ticketTypes.flatMap(
		(t) => figuresByType.get(t.id) ?? [],
	);

	const exceptionEntries = (
		[
			{ kind: "refundedOrders", count: refundedOrders },
			{ kind: "authorisedNeverPaid", count: authorisedNeverPaid },
			{ kind: "cancelledOnPaid", count: cancelledOnPaid },
		] satisfies readonly ExceptionEntry[]
	).filter((e) => e.count > 0);

	return {
		eventId,
		lifecycle,
		now,
		hasTicketTypes: ticketTypes.length > 0,
		publicShopEmpty:
			ticketTypes.length > 0 &&
			!ticketTypes.some((t) => TicketType.resolveClaimableBy(t) === "buyer"),
		totalCapacity,
		totalSold,
		soldTickets,
		guestTickets,
		grossTakenCents,
		vatOwedCents,
		governingVatRate: vatRates.size === 1 ? ([...vatRates][0] ?? null) : null,
		feeReportedCents,
		feeOrdersTotal,
		feeOrdersReported,
		unattributableOrders,
		youKeepCents:
			feeOrdersTotal > 0 && feeOrdersReported === 0
				? null
				: grossTakenCents - vatOwedCents - feeReportedCents,
		authorisedAmount,
		last7d,
		prev7d,
		daysToGo,
		typeRows,
		figuresByType,
		exceptionEntries,
		refundedCancelledCount: refundedOrders + cancelledOnPaid,
		validTickets,
		redeemedTickets,
		arrivalsLast15Min,
		attendanceKnown: redeemedTickets > 0,
		// Bounced first, newest-first within each group. A hard-bounced email is
		// the one order state that needs a person to do something — the buyer
		// is holding nothing, and only an operator can fix the address and
		// resend — so it is the one thing that outranks recency. Sorted here
		// rather than in the view because every consumer of this view model
		// wants the same order, and a list that re-sorts itself per surface is
		// a list an operator cannot learn.
		orders: orders.toSorted((a, b) => {
			const byBounce =
				Number(b.emailDeliveryStatus === "hard_bounced") -
				Number(a.emailDeliveryStatus === "hard_bounced");
			if (byBounce !== 0) return byBounce;
			return (
				b.authorisedAt.toDate().getTime() - a.authorisedAt.toDate().getTime()
			);
		}),
		ticketsByOrder,
	};
}

// ── Reactive wiring ──────────────────────────────────────────────────────────

/** Which event `<event-console>` currently mounts — set on connect, cleared
 *  on disconnect, mirroring ticket-types.state.ts's own ticketTypesEventId$
 *  side-channel shape. */
export const consoleEventId = state<string | null>(
	"events/console/eventId",
).memory(null);

// Each destination's segment in the console's address, keyed by the tag its
// route mounts — the tag stays its identity in `area.on("event-console")`.
export const CONSOLE_SEGMENT = {
	"event-console-dashboard": "dashboard",
	"event-console-orders": "orders",
	"event-console-analytics": "analytics",
	"event-console-edit": "edit",
	"voucher-list": "codes",
	"event-console-campaign": "promotion",
	"event-console-checkin": "check-in",
} as const;

export type ConsoleDestination = keyof typeof CONSOLE_SEGMENT;

/** Where `<event-console>`'s host addresses it — `null` where the host names
 *  no address (the studio), and its destinations push no path. */
export const consoleAddress = state<string | null>(
	"events/console/address",
).memory(null);

/** The push that opens a console destination, at its address under the
 *  console's own where the host names one. */
export function consoleDestination(tag: ConsoleDestination) {
	const address = consoleAddress.value;
	return {
		area: "event-console",
		component: CONSOLE_SEGMENT[tag],
		...(address ? { path: `${address}/${CONSOLE_SEGMENT[tag]}` } : {}),
	};
}

/** This event's own orders subcollection — written by web's
 *  console.sync.ts, which owns the Firestore listener; empty
 *  whenever the sync's gate (canViewSaleReports + a mounted organization
 *  and event) is closed; `consoleOrdersStatus` says how far it stands. */
export const consoleOrders = state<readonly OrderRow[]>(
	"events/console/orders",
).memory([]);

/** This event's own tickets subcollection — same writer/gate as
 *  consoleOrders. */
export const consoleTickets = state<readonly TicketRow[]>(
	"events/console/tickets",
).memory([]);

/** Where one console read stands for the mounted organization and event:
 *  - `loading` — no complete answer yet: no scope mounted, the listener still
 *    connecting, or its chunks still landing. The rows cell may hold a part.
 *  - `ready` — every chunk has landed: the rows cell is this scope's complete
 *    answer to the listener's query, and the only state a figure, a count or
 *    an empty list may be read from.
 *  - `denied` — this session's permission closes the gate, so no listener runs
 *    and an empty rows cell means "cannot see", never "none".
 *  - `stale` — the listener dropped and is reattaching: the rows cell still
 *    holds this scope's last complete answer, read as `ready` is, but no
 *    longer live.
 *  - `error` — the listener spent its retry budget; the rows on hand are not
 *    this scope's answer.
 *  A switch of organization or event returns both reads to `loading` (or
 *  `denied`) in the same step that empties their rows. */
export type ConsoleReadStatus =
	| "loading"
	| "ready"
	| "stale"
	| "denied"
	| "error";

/** Whether a read's rows are its scope's complete answer — live (`ready`) or
 *  the last one held while the listener reattaches (`stale`). */
export function consoleRowsAnswered(status: ConsoleReadStatus): boolean {
	return status === "ready" || status === "stale";
}

/** The orders read. Rides `tickets.reports.view`. On the standalone guest
 *  route its query is that event's guest orders alone, so that route returns
 *  every console cell to unanswered when it unmounts: a `ready` it leaves
 *  behind would let a whole-event figure sum guest-only rows. */
export const consoleOrdersStatus = state<ConsoleReadStatus>(
	"events/console/orders-status",
).memory("loading");

/** The tickets read. Rides `tickets.checkin.scan` alone, because Ticket's own
 *  `list` rule admits nothing else — so it can be `denied` while orders are
 *  `ready`, and the reverse. */
export const consoleTicketsStatus = state<ConsoleReadStatus>(
	"events/console/tickets-status",
).memory("loading");

/** Whether a figure or row on the console is the last answer held while a
 *  read reattaches, so the surface says it may be out of date. */
export const consoleNotLive = computed(
	() =>
		consoleOrdersStatus.value === "stale" ||
		consoleTicketsStatus.value === "stale",
);

/** The organization and event the console's two reads are answering for,
 *  written with their statuses on every re-scope, so a reader re-indexing
 *  their rows tags them with the scope they belong to rather than with
 *  whichever scope is current when it happens to run. */
export const consoleScope = state<{
	readonly orgId: string | null;
	readonly eventId: string | null;
}>("events/console/scope").memory({ orgId: null, eventId: null });

/** Where both reads start when the gates or the scope change: `loading`
 *  behind an open gate, `denied` behind a closed one — except while the
 *  session is binding to a new organization, when a closed gate is a role not
 *  yet read, so `loading` too. A listener opens only for a whole scope
 *  (organization and event) behind at least one open gate. */
export function consoleReadsOnRescope(
	canOrders: boolean,
	canTickets: boolean,
	scoped: boolean,
	binding = false,
): {
	readonly orders: ConsoleReadStatus;
	readonly tickets: ConsoleReadStatus;
	readonly listen: boolean;
} {
	return {
		orders: canOrders || binding ? "loading" : "denied",
		tickets: canTickets || binding ? "loading" : "denied",
		listen: scoped && (canOrders || canTickets),
	};
}

/** Whether tickets are arriving at all (`loading` or `ready`). A POSITIVE
 *  count or a search over ticket codes holds from the first chunk; a zero or a
 *  per-ticket consequence needs `consoleTicketsStatus === "ready"`. */
export const consoleTicketsListening = computed(
	() =>
		consoleTicketsStatus.value === "loading" ||
		consoleRowsAnswered(consoleTicketsStatus.value),
);

/** The dashboard's read model. `now` is read at scan time (whenever a
 *  tracked signal changes) rather than a ticking clock — an admin console
 *  reporting "as of the last refresh" is the accepted trade against a
 *  live-updating timer no other admin surface in this app runs either. */
export const consoleViewModel = computed<ConsoleViewModel | null>(() => {
	const eventId = consoleEventId.value;
	if (!eventId) return null;
	const event = eventsState.value.find((e) => e.id === eventId);
	if (!event) return null;
	const locale = uiLanguage();
	return buildConsoleViewModel(
		eventId,
		{ start: event.schedule.start.toDate(), end: event.schedule.end.toDate() },
		// The per-type sold/remaining table names each type in the operator's
		// own UI language; buildConsoleViewModel counts and divides, so the
		// label it carries is already the one the screen renders.
		ticketTypesState.value.map((t) => {
			const entry = TicketType.resolveEntryWindow(t, event);
			return {
				...t,
				name: resolveLocalizedText(t.nameByLocale, t.name, locale),
				entryWindow: {
					opensAt: entry.opensAt.toMillis(),
					closesAt: entry.closesAt.toMillis(),
				},
				timezone: TicketType.resolveEffectiveTimezone(
					t,
					event,
					Object.fromEntries(locationsState.value.map((l) => [l.id, l])),
				),
			};
		}),
		consoleOrders.value,
		consoleTickets.value,
		new Date(Date.now()),
	);
});

/** The view model once the orders read is `ready`, else null — every sale
 *  figure (issued, sold, money, pace, per-type sell-through) reads from here,
 *  so none is stated off a partial or absent set. */
export const consoleSaleFigures = computed<ConsoleViewModel | null>(() =>
	consoleRowsAnswered(consoleOrdersStatus.value)
		? consoleViewModel.get()
		: null,
);

/** The view model for door figures (checked-in, valid, arrivals), or null
 *  unless the tickets read is `ready` — those figures count tickets alone, so
 *  the orders read neither holds nor gates them. */
export const consoleDoorFigures = computed<ConsoleViewModel | null>(() =>
	consoleRowsAnswered(consoleTicketsStatus.value)
		? consoleViewModel.get()
		: null,
);

/** Sold and revenue per ticket-type id for the mounted event — every kind, so
 *  an addon states the number the same counting pass already produced for it —
 *  or null where no figure can be stated at all. Null and an empty map are
 *  opposite answers: a surface that cannot read orders states nothing, where a
 *  zero it invented draws as an empty ring beside real ones. */
export const consoleFiguresByType = computed<ReadonlyMap<
	string,
	TypeRow
> | null>(() => consoleSaleFigures.get()?.figuresByType ?? null);

export const consoleEvent = computed(
	() => eventsState.value.find((e) => e.id === consoleEventId.value) ?? null,
);

/** This event's day dimension — every calendar day at least one ticket type
 *  opens the door on, derived from each type's own resolved entry window and
 *  never stored (TicketType.programmeDaysOf). A type covering the whole event
 *  contributes none, so an event whose types carry no entry window at all —
 *  the common case — yields no days and the dashboard renders exactly the
 *  block it rendered before the day dimension existed.
 *
 *  Unlike the shop's own days, a day already past is KEPT: the console is a
 *  record of what sold, and hiding yesterday would hide yesterday's sales
 *  from the only surface that reports them. There is no selected day here at
 *  all — <programme-calendar> shows every day at once, because an operator is
 *  accounting for the whole programme rather than standing on one day. */
export const consoleProgrammeDays = computed<
	readonly TicketType.ProgrammeDay[]
>(() => {
	const event = consoleEvent.get();
	if (event === null) return [];
	return TicketType.programmeDaysOf(
		(consoleViewModel.get()?.typeRows ?? [])
			.filter((r) => !TicketType.coversWholeEvent(r.entryWindow, event))
			.map((r) => ({ ...r.entryWindow, timezone: r.timezone })),
	);
});

/** The guest-order list — origin == 'guest' selected from whatever orders
 *  are already loaded, bounce-first then newest-first (consoleViewModel's
 *  own sort) — or null until the orders read is `ready`, so the list never
 *  states "no guest orders", or a count, off a set still landing. Correct on
 *  both guest-issue.view.ts's hosts: the standalone route's own listener
 *  already scopes to origin == 'guest' (so this is a no-op filter there), and
 *  the console's Guests destination selects out of the event's full order set
 *  event-console-sync already owns — no second listener is ever opened for
 *  it. */
export const guestOrderRows = computed<readonly OrderRow[] | null>(
	() =>
		consoleSaleFigures.get()?.orders.filter((o) => o.origin === "guest") ??
		null,
);
